export const M_PER_FT = 0.3048;
export const FT_PER_M = 1 / M_PER_FT; // ≈ 3.28084
export const M_PER_NM = 1852;
export const L_PER_GAL = 3.785411784;
export const GAL_PER_L = 1 / L_PER_GAL; // ≈ 0.26417
/** Static rate, not a live one: update it here by hand when it drifts too far. */
export const EUR_PER_USD = 0.9;
export const USD_PER_EUR = 1 / EUR_PER_USD;

export type LengthUnit = 'ft' | 'm';
export type VolumeUnit = 'gal' | 'L';

/** Display currency for money the user types in by hand. Canonical storage is always USD. */
export type CurrencyUnit = '$' | '€';

/** The volume unit as the vessel service spells it on the daily and navigation logs. */
export type ApiVolumeUnit = 'l' | 'gal';

export function toApiVolumeUnit(unit: VolumeUnit): ApiVolumeUnit {
  return unit === 'L' ? 'l' : 'gal';
}

/** Anything but `l` reads as gallons — what the service assumes when a log names no unit. */
export function fromApiVolumeUnit(unit: string | undefined): VolumeUnit {
  return unit === 'l' ? 'L' : 'gal';
}

/** Speed as the crew keys it into a navigation log entry. */
export type SpeedUnit = 'kn' | 'km/h' | 'mph';
export const SPEED_UNITS: SpeedUnit[] = ['kn', 'km/h', 'mph'];
/** Exact: a nautical mile is 1852 m. */
export const KMH_PER_KN = M_PER_NM / 1000;
export const KMH_PER_MPH = 1.609344;

/** The speed unit as the vessel service spells it on a navigation log entry. */
export type ApiSpeedUnit = 'kn' | 'kmh' | 'mph';

export function toApiSpeedUnit(unit: SpeedUnit): ApiSpeedUnit {
  return unit === 'km/h' ? 'kmh' : unit;
}

/** Undefined for an entry written before the unit could be picked. */
export function fromApiSpeedUnit(unit: string | undefined): SpeedUnit | undefined {
  if (unit === 'kmh') return 'km/h';
  if (unit === 'kn' || unit === 'mph') return unit;
  return undefined;
}

const KMH_PER: Record<SpeedUnit, number> = { kn: KMH_PER_KN, 'km/h': 1, mph: KMH_PER_MPH };

/** Convert a value between two speed units (for the dropdown switch). */
export function convertSpeed(value: number, from: SpeedUnit, to: SpeedUnit): number {
  if (from === to) return value;
  return (value * KMH_PER[from]) / KMH_PER[to];
}

export function round1(v: number): number {
  return Math.round(v * 10) / 10;
}

/** Money rounds to cents, not to one decimal. */
export function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

// ── display conversions (canonical → preferred) ──────────────────────────────

/** Convert a canonical FEET value to the preferred display unit. */
export function toDisplayLength(canonicalFt: number, pref: LengthUnit): number {
  return pref === 'm' ? canonicalFt * M_PER_FT : canonicalFt;
}

/** Convert a canonical METRES value to the preferred display unit. */
export function toDisplayLengthFromM(canonicalM: number, pref: LengthUnit): number {
  return pref === 'ft' ? canonicalM * FT_PER_M : canonicalM;
}

/** Convert a canonical LITRES value to the preferred display unit. */
export function toDisplayVolume(canonicalL: number, pref: VolumeUnit): number {
  return pref === 'gal' ? canonicalL * GAL_PER_L : canonicalL;
}

/** Convert a canonical USD amount to the preferred display currency. */
export function toDisplayCurrency(canonicalUsd: number, pref: CurrencyUnit): number {
  return pref === '€' ? canonicalUsd * EUR_PER_USD : canonicalUsd;
}

// ── save conversions (preferred → canonical) ──────────────────────────────────

/** Convert a display value (preferred unit) back to canonical FEET. */
export function toCanonicalFt(display: number, pref: LengthUnit): number {
  return pref === 'm' ? display * FT_PER_M : display;
}

/** Convert a display value (preferred unit) back to canonical METRES. */
export function toCanonicalM(display: number, pref: LengthUnit): number {
  return pref === 'ft' ? display * M_PER_FT : display;
}

/** Convert a display value (preferred unit) back to canonical LITRES. */
export function toCanonicalL(display: number, pref: VolumeUnit): number {
  return pref === 'gal' ? display * L_PER_GAL : display;
}

/** Convert a display amount (preferred currency) back to canonical USD. */
export function toCanonicalUsd(display: number, pref: CurrencyUnit): number {
  return pref === '€' ? display * USD_PER_EUR : display;
}

// ── cross-unit conversion (for in-place unit switching) ──────────────────────

/** Convert a value between two length units (for the dropdown switch). */
export function convertLength(value: number, from: LengthUnit, to: LengthUnit): number {
  if (from === to) return value;
  return from === 'ft' ? value * M_PER_FT : value * FT_PER_M;
}

/** Convert a value between two volume units (for the dropdown switch). */
export function convertVolume(value: number, from: VolumeUnit, to: VolumeUnit): number {
  if (from === to) return value;
  return from === 'gal' ? value * L_PER_GAL : value * GAL_PER_L;
}

/**
 * Convert a price per volume unit between two units (for the dropdown switch).
 * It runs the other way to a volume: $/gal → $/L divides by 3.785.
 */
export function convertPerVolume(value: number, from: VolumeUnit, to: VolumeUnit): number {
  return convertVolume(value, to, from);
}

/** Convert an amount between two currencies (for the dropdown switch). */
export function convertCurrency(value: number, from: CurrencyUnit, to: CurrencyUnit): number {
  if (from === to) return value;
  return from === '$' ? value * EUR_PER_USD : value * USD_PER_EUR;
}

/** Round for display: 118 rather than 117.6. */
export const formatNauticalMiles = (nm: number): string => `${Math.round(nm)} nm`;
